















































import {
    computed,
    defineComponent,
    ref,
    reactive,
    watch,
    toRefs,
} from '@vue/composition-api';
import { useApolloModel } from 'vue-apollo-model';
import { init as ssoSDKInit, getQRLoginInfo, getUserLoginInfo, scanQRAcceptResult, BaseRes, QRCodeResult  } from '@ks/sso';
import { QrCodeModel, QueryInfo } from './model';
import infraConfig from './config';
import { portalSid, envSSOSDK } from '@/common/consts';

export default defineComponent({
    name: 'ks-qr-code',
    props: {
        size: {
            type: String,
            default: '150px',
        },
    },
    setup(props, context) {
        const status = reactive({
            loading: false,
            timeout: false,
            scan: false,
            accept: false,
            error: false,
            abandon: false,
            qrCode: '',
        });
        const qrCodeSize = ref(props.size);
        const inlineStyle = computed(() => {
            return `height: ${props.size};width: ${props.size}`;
        });
        const qrCodeUrl = computed(() => {
            return `data:image/PNG;base64,${status.qrCode}`;
        });
        const qrCodeModel = useApolloModel(QrCodeModel);
        const reset = () => {
            status.qrCode = '';
            status.timeout = false;
            status.error = false;
            status.scan = false;
            status.abandon = false;
        };
        const { app_id, scope, redirect_uri, state }: QueryInfo =
            context.parent!.$route.query as any;

        watch(
            () =>
                qrCodeModel.appNameQuery.loading,
            async (v) => {
                status.loading = v;
            },
            {
                immediate: true,
            },
        );

        const tipsText = computed(() => {
            if (status.error) {
                return '二维码获取失败 请重新尝试';
            }
            if (status.abandon) {
                return '已操作取消授权';
            }
            if (status.timeout) {
                return '二维码失效 请点击刷新';
            }
            if (status.accept) {
                return '已确认授权';
            }
            if (status.scan) {
                return '扫码成功 请在快手中完成确认登录';
            }
            return `请用快手扫描二维码登录 “${qrCodeModel.appName}”`;
        });

        const checkScan = async (qrLoginToken: string, qrLoginSignature: string) => {
            try {
                return await getUserLoginInfo({qrLoginToken, qrLoginSignature}) as BaseRes & QRCodeResult;
            } catch (e) {
                throw new Error('timeout');
            }
        };

        const checkAccept = async (qrLoginToken: string, qrLoginSignature: string) => {
            try {
                const token = await scanQRAcceptResult({qrLoginToken, qrLoginSignature})
                if(token.result === 1) {
                    context.emit('accept', token.serviceOwnToken);
                } else {
                    context.emit('abandon');
                    throw new Error('abandon');
                }
            }catch (e) {
                throw new Error('abandon');
            }
        };

        const init = async () => {
            reset();
            try {
                let param;
                // 扫码功能调用前必须进行sso初始化，并传递相关参数
                ssoSDKInit({
                    sid: portalSid,
                    env: envSSOSDK,
                    qrType: infraConfig.qrType,
                    kpn: infraConfig.kpn,
                    serviceOwnParams: JSON.stringify({
                        appId: app_id,
                        scope,
                        redirect_uri,
                        state,
                    }),
                    // 为 true 的话，扫码授权就会调用 callback，用来登录，但是授权场景下，扫码不用做登陆
                    // 因此SDK新增了方法：scanQRAcceptResult
                    kuaishouAuth: false,
                    disableQuickLogin: true,
                    enableSig4: true,
                })

                // 第一步：获取二维码信息
                const { qrLoginSignature, qrLoginToken, imageData} = await getQRLoginInfo();

                status.qrCode = imageData;

                // 第二步：等待用户扫码
                // 这一步报错，会提示 timeout
                const userInfo = await checkScan(qrLoginToken, qrLoginSignature);

                status.scan = true;

                // 第三步：用户扫码回调
                // 这块扫码有两套 新的扫码优先使用 scanResult 返回的签名
                // 见下方文档中：扫码登录示例
                // https://docs.corp.kuaishou.com/k/home/VHb3TfJ8laDI/fcAChnT6DXZ5m3xwQU1ouUAqA#section=h.6jwysngnj6fr
                await checkAccept(qrLoginToken, userInfo.qrLoginSignature || qrLoginSignature);

                status.accept = true;
            } catch (e) {
                let message: string;
                if (e instanceof Error) {
                    message = e.message;
                } else if (typeof e === 'object' && e !== null && 'message' in e) {
                    message = e.message as string;
                } else {
                    message = String(e);
                }

                if (message === 'timeout') {
                    status.scan = false;
                    status.timeout = true;
                } else if (message === 'abandon') {
                    status.scan = false;
                    status.abandon = true;
                } else {
                    status.scan = false;
                    status.error = true;
                }
            }
        };
        init();
        return {
            init,
            qrCodeUrl,
            qrCodeSize,
            inlineStyle,
            tipsText,
            ...toRefs(status),
        };
    },
});
